import defaultConfig from '../config/configDefault';

const ensureOpenGraphLocale = locale => {
  switch (locale) {
    case 'en':
      return 'en_US';
    default:
      return locale;
  }
};

/**
 * These will be used with Helmet <meta {...openGraphMetaProps} />
 */
export const openGraphMetaProps = data => {
  const {
    openGraphType,
    socialSharingTitle,
    socialSharingDescription,
    published,
    updated,
    url,
    locale,
    facebookImages,
    facebookAppId,
    marketplaceName,
  } = data;

  if (!(socialSharingTitle && socialSharingDescription && openGraphType && url && facebookImages)) {
    return [];
  }

  const openGraphMeta = [
    { property: 'og:description', content: socialSharingDescription },
    { property: 'og:title', content: socialSharingTitle },
    { property: 'og:type', content: openGraphType },
    { property: 'og:url', content: url },
    { property: 'og:locale', content: ensureOpenGraphLocale(locale) },
  ];

  if (facebookImages && facebookImages.length > 0) {
    facebookImages.forEach(i => {
      openGraphMeta.push({
        property: 'og:image',
        content: i.url,
      });

      if (i.width && i.height) {
        openGraphMeta.push({ property: 'og:image:width', content: i.width });
        openGraphMeta.push({ property: 'og:image:height', content: i.height });
      }
    });
  }

  if (marketplaceName) {
    openGraphMeta.push({ property: 'og:site_name', content: marketplaceName });
  }

  if (facebookAppId) {
    openGraphMeta.push({ property: 'fb:app_id', content: facebookAppId });
  }

  if (published) {
    openGraphMeta.push({ property: 'article:published_time', content: published });
  }

  if (updated) {
    openGraphMeta.push({ property: 'article:modified_time', content: updated });
  }

  return openGraphMeta;
};

/**
 * These will be used with Helmet <meta {...twitterMetaProps} />
 */
export const twitterMetaProps = data => {
  const {
    socialSharingTitle,
    socialSharingDescription,
    twitterHandle,
    twitterImages,
    url,
    marketplaceRootURL,
    siteTwitterHandle,
  } = data;

  if (!(socialSharingTitle && socialSharingDescription && url)) {
    return [];
  }

  const twitterMeta = [
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: socialSharingTitle },
    { name: 'twitter:description', content: socialSharingDescription },
    { name: 'twitter:url', content: url },
  ];

  if (siteTwitterHandle) {
    twitterMeta.push({ name: 'twitter:site', content: siteTwitterHandle });
  }

  if (twitterImages && twitterImages.length > 0) {
    twitterImages.forEach(i => {
      twitterMeta.push({
        name: 'twitter:image',
        content: i.url,
      });
    });
  }

  if (twitterHandle) {
    // TODO: If we want to connect providers twitter account on ListingPage
    // we needs to get this info among listing data (API support needed)
    twitterMeta.push({ name: 'twitter:creator', content: twitterHandle });
  }

  if (marketplaceRootURL) {
    twitterMeta.push({ name: 'twitter:domain', content: marketplaceRootURL });
  }

  return twitterMeta;
};

/**
 * These will be used with Helmet <meta {...metaTagProps} />
 * Creates data for Open Graph and Twitter meta tags.
 */
export const metaTagProps = (tagData, config) => {
  const {
    marketplaceRootURL,
    facebookAppId,
    marketplaceName,
    siteTwitterHandle,
    googleSearchConsole,
  } = config;

  const author = tagData.author || marketplaceName;
  const googleSiteVerification = googleSearchConsole?.googleSiteVerification;
  const googleSiteVerificationMaybe = googleSiteVerification
    ? [{ name: 'google-site-verification', content: googleSiteVerification }]
    : [];
  const defaultMeta = [
    { name: 'description', content: tagData.description },
    { name: 'author', content: author },
    ...googleSiteVerificationMaybe,
  ];

  const openGraphMeta = openGraphMetaProps({
    ...tagData,
    facebookAppId,
    marketplaceName,
  });

  const twitterMeta = twitterMetaProps({
    ...tagData,
    marketplaceRootURL,
    siteTwitterHandle,
  });

  return [...defaultMeta, ...openGraphMeta, ...twitterMeta];
};

/**
 * Generate comprehensive meta tags for smart home marketplace SEO
 * @param {Object} options - SEO options
 * @returns {Array} Array of meta tag props
 */
export const generateSmartHomeMetaTags = (options = {}) => {
  const {
    title,
    description,
    keywords,
    image,
    url,
    type = 'website',
    published,
    updated,
    author = 'Neokasa',
    category = 'Smart Home Technology',
    location = 'United States'
  } = options;

  const defaultKeywords = defaultConfig.seo?.defaultKeywords || 'smart home, smart home devices, smart home USA';
  const combinedKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;
  
  const metaTags = [
    // Basic meta tags
    { name: 'description', content: description || defaultConfig.seo?.defaultDescription },
    { name: 'keywords', content: combinedKeywords },
    { name: 'author', content: author },
    { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
    
    // Location targeting for USA market
    { name: 'geo.region', content: 'US' },
    { name: 'geo.placename', content: location },
    { name: 'geo.position', content: '39.8283;-98.5795' }, // Center of USA
    { name: 'ICBM', content: '39.8283, -98.5795' },
    
    // Smart home specific meta tags
    { name: 'category', content: category },
    { name: 'application-name', content: 'Neokasa' },
    { name: 'apple-mobile-web-app-title', content: 'Neokasa' },
    
    // Open Graph tags
    { property: 'og:type', content: type },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description || defaultConfig.seo?.defaultDescription },
    { property: 'og:url', content: url },
    { property: 'og:site_name', content: 'Neokasa' },
    { property: 'og:locale', content: 'en_US' },
    { property: 'og:image', content: image },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:alt', content: 'Neokasa - Best Smart Home Devices' },
    
    // Twitter Card tags
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@smarthomeusa' },
    { name: 'twitter:creator', content: '@smarthomeusa' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description || defaultConfig.seo?.defaultDescription },
    { name: 'twitter:image', content: image },
    { name: 'twitter:image:alt', content: 'Neokasa - Best Smart Home Devices' },
    
    // Article specific meta tags
    ...(type === 'article' ? [
      { property: 'article:author', content: author },
      { property: 'article:section', content: category },
      { property: 'article:published_time', content: published },
      { property: 'article:modified_time', content: updated },
      { property: 'article:tag', content: combinedKeywords }
    ] : []),
    
    // Smart home specific structured data hints
    { name: 'smart-home:category', content: category },
    { name: 'smart-home:location', content: location },
    { name: 'smart-home:brand', content: 'Neokasa' }
  ];

  return metaTags;
};

/**
 * Generate structured data for smart home marketplace
 * @param {Object} options - Structured data options
 * @returns {Object} Structured data object
 */
export const generateSmartHomeStructuredData = (options = {}) => {
  const {
    type = 'WebPage',
    name,
    description,
    url,
    image,
    breadcrumbs = [],
    offers = [],
    reviews = []
  } = options;

  const baseData = {
    '@context': 'https://schema.org',
    '@type': type,
    '@id': url,
    name: name || 'Neokasa',
    description: description || defaultConfig.seo?.defaultDescription,
    url: url,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url
    },
    publisher: {
      '@type': 'Organization',
      name: 'Neokasa',
      url: 'https://www.neokasa.fr',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.neokasa.fr/logo.png'
      },
      sameAs: Object.values(defaultConfig.seo?.socialMedia || {})
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://www.neokasa.fr/products?q={search_term_string}'
      },
      'query-input': 'required name=search_term_string'
    }
  };

  // Add breadcrumbs if provided
  if (breadcrumbs.length > 0) {
    baseData.breadcrumb = {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.url
      }))
    };
  }

  // Add offers if provided
  if (offers.length > 0) {
    baseData.offers = offers.map(offer => ({
      '@type': 'Offer',
      price: offer.price,
      priceCurrency: offer.currency || 'USD',
      availability: offer.availability || 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'Neokasa'
      }
    }));
  }

  // Add reviews if provided
  if (reviews.length > 0) {
    baseData.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length,
      reviewCount: reviews.length
    };
    baseData.review = reviews.map(review => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.author
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating
      },
      reviewBody: review.content
    }));
  }

  return baseData;
};
